if (!customElements.get('salud-cart-drawer-v2')) {
  class SaludCartDrawerV2 extends HTMLElement {
    connectedCallback() {
      this.cartStateElement = this.querySelector('[data-cart-state]');
      this.itemsElement = this.querySelector('[data-cart-items]');
      this.giftsSectionElement = this.querySelector('[data-cart-gifts-section]');
      this.giftItemsElement = this.querySelector('[data-cart-gifts]');
      this.itemCountElement = this.querySelector('[data-cart-count]');
      this.checkoutTotalElement = this.querySelector('[data-cart-checkout-total]');
      this.checkoutOriginalTotalElement = this.querySelector('[data-cart-checkout-original-total]');
      this.footerElement = this.querySelector('[data-cart-footer]');
      this.checkoutBlockElement = this.querySelector('[data-cart-checkout-block]');
      this.checkoutElement = this.querySelector('[data-cart-checkout]');
      this.emptyElement = this.querySelector('[data-cart-empty]');
      this.statusElement = this.querySelector('[data-cart-status]');
      this.progressElement = this.querySelector('[data-cart-progress]');
      this.progressTextElement = this.querySelector('[data-cart-progress-text]');
      this.progressTrackElement = this.querySelector('[data-cart-progress-track]');
      this.packsProgressElement = this.querySelector('[data-cart-packs-progress]');
      this.packsProgressTrackElement = this.querySelector('[data-cart-packs-progress-track]');
      this.packsProgressStepsElement = this.querySelector('[data-cart-packs-progress-steps]');
      this.panel = this.querySelector('.cart-drawer-v2__panel');
      this.offersElement = this.querySelector('[data-subscription-offers]');
      this.stringsElement = this.querySelector('[data-cart-strings]');
      this.planOptionsByVariant = new Map();
      this.sellingPlansByLineKey = new Map();
      this.productTitlesById = new Map();
      this.productTypesById = new Map();
      this.compareAtByVariant = new Map();
      this.otpPricingByVariant = new Map();
      this.productImagesByVariant = new Map();
      this.pendingMutations = new Map();
      this.manualDeliverySelections = new Map();
      this.renderedCartItems = new WeakMap();
      this.serverLineKeys = new Map();
      this.requestQueue = Promise.resolve();
      this.failedCartUpdates = 0;
      this.cartResponseVersion = 0;
      this.optimisticAdds = new Map();
      this.addedItemOrder = new Map();
      this.nextAddedItemOrder = 0;
      this.activeElement = null;
      this.cart = this.readJson(this.cartStateElement, { items: [], item_count: 0, total_price: 0, currency: 'USD' });
      this.offers = this.readJson(this.offersElement, []);
      this.strings = this.readJson(this.stringsElement, {});
      this.capturePlanOptions();
      this.serverCart = structuredClone(this.cart);
      this.captureProductTitles();
      this.captureRecommendations();
      this.onClick = this.handleClick.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onCartRefresh = this.handleCartRefresh.bind(this);
      this.onGiftInventoryChange = () => this.render();
      this.onChange = this.handleChange.bind(this);
      this.onFocusOut = (event) => {
        if (event.target?.dataset?.cartAction !== 'change-frequency') return;
        queueMicrotask(() => {
          if (this.isConnected && this.deferredFrequencyUpdate) this.render();
        });
      };
      this.onPageShow = (event) => {
        if (!event.persisted || !this.checkoutPending) return;
        this.setCheckoutLoading(false);
        this.finishCartUpdateBatch();
        this.refreshFromShopify();
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('cart:refresh', this.onCartRefresh);
      document.addEventListener('subscription-gifts:change', this.onGiftInventoryChange);
      window.addEventListener('pageshow', this.onPageShow);
      if (window.SaludSubscriptionCartSync) {
        if (!this.enforceMinimumSubscriptionQuantity()) this.render();
      } else {
        this.render();
        this.onMinimumReady = () => this.enforceMinimumSubscriptionQuantity();
        document.addEventListener('DOMContentLoaded', this.onMinimumReady, { once: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onMinimumReady);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('cart:refresh', this.onCartRefresh);
      document.removeEventListener('subscription-gifts:change', this.onGiftInventoryChange);
      window.removeEventListener('pageshow', this.onPageShow);
      this.close(false);
    }

    readJson(element, fallback) {
      try {
        return JSON.parse(element?.textContent || '');
      } catch (error) {
        return fallback;
      }
    }

    capturePlanOptions() {
      this.querySelectorAll('[data-cart-item]').forEach((row) => {
        const variantId = String(row.dataset.variantId || '');
        const key = String(row.dataset.key || '');
        const sellingPlanId = String(row.dataset.sellingPlanId || '');
        const line = this.cart.items.find((item) => String(item.key) === key);
        if (line && sellingPlanId) {
          line.selling_plan_id = sellingPlanId;
          this.sellingPlansByLineKey.set(key, sellingPlanId);
        }
        const plans = this.readJson({ textContent: row.dataset.sellingPlans }, []);
        if (variantId && plans.length) this.planOptionsByVariant.set(variantId, plans);
        const compareAtPrice = Number(row.dataset.compareAtPrice || 0);
        this.otpPricingByVariant.set(variantId, {
          price: Number(row.dataset.basePrice),
          percentage: Number(row.dataset.otpSalePercentage) || 0,
        });
        if (variantId && compareAtPrice > 0) this.compareAtByVariant.set(variantId, compareAtPrice);
        const image = row.querySelector('.cart-drawer-v2__image');
        if (variantId && image?.src) this.productImagesByVariant.set(variantId, image.currentSrc || image.src);
      });
    }

    captureProductTitles() {
      this.querySelectorAll('[data-cart-item][data-product-title]').forEach((row) => {
        const productId = row.dataset.productId;
        if (productId && row.dataset.productTitle) this.productTitlesById.set(String(productId), row.dataset.productTitle);
        if (productId && row.dataset.productType) this.productTypesById.set(String(productId), row.dataset.productType);
      });
    }

    captureRecommendations() {
      this.recommendations = [...this.querySelectorAll('[data-recommendation-card]')].map((card) => {
        const button = card.querySelector('[data-cart-action="add-recommendation"]');
        const plans = this.readJson({ textContent: button.dataset.sellingPlans }, []);
        this.planOptionsByVariant.set(String(button.dataset.variantId), plans);
        return { card, button, plans };
      });
    }

    recommendationQuantity() {
      return Math.max(1, this.minimumSubscriptionQuantity() - this.getSubscriptionPackQuantity());
    }

    recommendationPlan(plans) {
      const offer = this.offerForQuantity(this.getPackQuantity() + this.recommendationQuantity());
      return offer ? this.selectPlan(plans, offer) : plans[0] || null;
    }

    renderRecommendations() {
      const recommendationOffer = this.offerForQuantity(this.getSubscriptionPackQuantity() + this.recommendationQuantity());
      const discount = Number(recommendationOffer?.orderDiscount);
      this.querySelectorAll('[data-cart-recommendation-savings]').forEach((badge) => {
        badge.hidden = !Number.isFinite(discount) || discount <= 0;
        badge.textContent = badge.hidden ? ''
          : this.translate('cart_drawer_v2.recommendation_savings', 'Save {{ percent }}%').replace('{{ percent }}', String(discount));
      });
      const productIds = new Set((this.cart.items || []).map((item) => String(item.product_id || '')));
      const variantIds = new Set((this.cart.items || []).map((item) => String(item.variant_id || item.id)));
      let visibleCount = 0;
      let restoredCard = false;
      for (const { card, button, plans } of this.recommendations || []) {
        const plan = this.recommendationPlan(plans);
        const wasHidden = card.hidden;
        card.hidden = productIds.has(String(button.dataset.productId))
          || variantIds.has(String(button.dataset.variantId))
          || !plan;
        if (card.hidden) continue;
        button.disabled = this.checkoutPending === true || this.hasPendingRecommendation(button.dataset.variantId);
        if (wasHidden) restoredCard = true;
        visibleCount += 1;
        const price = Number(plan.price);
        const comparePrice = Math.max(Number(plan.compareAtPrice || 0), Number(button.dataset.productComparePrice || 0));
        card.querySelector('[data-recommendation-price]').textContent = this.formatMoney(price);
        const compareElement = card.querySelector('[data-recommendation-compare-price]');
        compareElement.textContent = this.formatMoney(comparePrice);
        compareElement.hidden = comparePrice <= price;
      }
      const section = this.querySelector('[data-cart-recommendations]');
      if (section) section.hidden = visibleCount === 0;
      if (restoredCard) {
        requestAnimationFrame(() => {
          this.querySelector('[data-cart-recommendations-track]')?.scrollTo({ left: 0, behavior: 'instant' });
        });
      }
      return visibleCount > 0;
    }

    hasPendingRecommendation(variantId) {
      return [...this.optimisticAdds.values()].some((added) => (Array.isArray(added) ? added : [added])
        .some((item) => item.interactivePendingAdd && String(item.variant_id || item.id) === String(variantId)));
    }

    isBlockedPendingItem(item) {
      return String(item.key).startsWith('pending-') && !item.interactivePendingAdd && !this.serverLineKeys.has(item.key);
    }

    handleDocumentClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const cartTrigger = target?.closest('#cart-icon-bubble, [data-cart-open]');
      if (!cartTrigger || !this.isConnected) return;
      event.preventDefault();
      this.open(cartTrigger);
    }

    handleClick(event) {
      const action = event.target instanceof Element ? event.target.closest('[data-drawer-close], [data-cart-action], [data-cart-checkout]') : null;
      if (!action) return;

      if (action.hasAttribute('data-cart-checkout')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.completeCheckout();
        return;
      }

      if (action.hasAttribute('data-drawer-close')) {
        this.close();
        return;
      }

      if (action.dataset.cartAction === 'recommend-prev' || action.dataset.cartAction === 'recommend-next') {
        this.scrollRecommendations(action.dataset.cartAction === 'recommend-next' ? 1 : -1);
        return;
      }

      if (this.checkoutPending) return;
      if (action.dataset.cartAction === 'add-recommendation') {
        this.addRecommendation(action);
        return;
      }

      const row = action.closest('[data-cart-item]');
      if (!row || row.dataset.pending === 'true') return;
      const key = row.dataset.key;
      const item = this.renderedCartItems.get(row)
        || this.cart.items.find((cartItem) => String(cartItem.key) === String(key));
      if (!item) return;

      switch (action.dataset.cartAction) {
        case 'increase':
          this.changeQuantity(item, Number(item.quantity) + 1);
          break;
        case 'decrease':
          this.changeQuantity(item, Math.max(0, Number(item.quantity) - 1));
          break;
        case 'remove':
          this.changeQuantity(item, 0);
          break;
        case 'subscribe':
          this.subscribeItem(item);
          break;
      }
    }

    handleChange(event) {
      if (this.checkoutPending) return;
      const select = event.target instanceof HTMLSelectElement ? event.target : null;
      if (select?.dataset.cartAction !== 'change-frequency') return;
      const row = select.closest('[data-cart-item]');
      if (!row || row.dataset.pending === 'true') return;
      const item = this.renderedCartItems.get(row)
        || this.cart.items.find((cartItem) => String(cartItem.key) === String(row.dataset.key));
      if (item) this.changeFrequency(item, select.value);
    }

    handleKeydown(event) {
      if (!this.hasAttribute('open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleCartRefresh(event) {
      if (event.detail?.cart?.items) {
        this.setCart(event.detail.cart);
      } else {
        this.refreshFromShopify();
      }
    }

    open(trigger) {
      this.activeElement = trigger || document.activeElement;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-v2-open');
      requestAnimationFrame(() => this.querySelector('.cart-drawer-v2__close')?.focus());
    }

    close(restoreFocus = true) {
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-v2-open');
      if (restoreFocus && this.activeElement?.isConnected) this.activeElement.focus();
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    beginOptimisticAdd(formData, productForm) {
      const variantId = String(formData.get('id') || '');
      if (!variantId) return null;
      const product = this.productFromForm(productForm, variantId);
      if (!product) return null;
      if (product.productId && product.title) this.productTitlesById.set(String(product.productId), product.title);
      if (product.productId && product.type) this.productTypesById.set(String(product.productId), product.type);
      if (product.compareAtPrice > 0) this.compareAtByVariant.set(variantId, product.compareAtPrice);

      const token = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
      const properties = {};
      for (const [name, value] of formData.entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match && value) properties[match[1]] = String(value);
      }
      const sellingPlanId = String(formData.get('selling_plan') || '');
      if (sellingPlanId) {
        const identity = this.cartItemDisplayIdentity({ variant_id: variantId, selling_plan: sellingPlanId, properties });
        const existing = (this.serverCart?.items || []).find((line) => !this.isGiftItem(line)
          && String(this.linePlanId(line) || '') === sellingPlanId
          && this.cartItemDisplayIdentity(line) === identity);
        if (existing) {
          if (existing.properties?.Discount != null && existing.properties.Discount !== '') {
            properties.Discount = String(existing.properties.Discount);
            formData.set('properties[Discount]', properties.Discount);
          } else {
            delete properties.Discount;
            formData.delete('properties[Discount]');
          }
        }
      }
      if (product.sellingPlans?.length) this.planOptionsByVariant.set(variantId, product.sellingPlans);
      this.otpPricingByVariant.set(variantId, { price: product.price, percentage: product.otpSalePercentage });

      const item = {
        key: token,
        id: Number(variantId),
        variant_id: Number(variantId),
        product_id: product.productId,
        handle: product.handle,
        title: product.title,
        product_title: product.title,
        variant_title: product.variantTitle,
        url: product.url,
        image: product.image,
        quantity,
        price: product.price,
        original_price: product.compareAtPrice || product.price,
        final_price: product.price,
        original_line_price: (product.compareAtPrice || product.price) * quantity,
        final_line_price: product.price * quantity,
        properties,
        selling_plan: formData.get('selling_plan') || null,
        selling_plan_allocation: null,
      };
      item.baselineQuantity = (this.cart.items || []).filter((line) => String(line.variant_id || line.id) === variantId && !String(line.key || '').startsWith('pending-')).reduce((total, line) => total + Number(line.quantity || 0), 0);

      if (item.selling_plan) {
        const plan = product.sellingPlans?.find((option) => String(option.id) === String(item.selling_plan));
        if (plan) item.selling_plan_allocation = { selling_plan: { id: plan.id, name: plan.name }, price: plan.price };
      }
      item.final_price = item.selling_plan_allocation?.price ?? this.optimisticOneTimePrice(item, product.price);
      item.final_line_price = item.final_price * quantity;

      this.optimisticAdds.set(token, item);
      this.prioritizeAddedItems([item]);
      this.cart.items = [item, ...(this.cart.items || [])];
      this.updateTotalsOptimistically();
      this.render();
      this.open();
      this.announce(this.translate('cart_drawer_v2.item_added', 'Item added to your bag'));
      return token;
    }

    beginOptimisticAddProducts(products) {
      const token = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const items = (products || []).map((product, index) => {
        const basePrice = Number(product.price || 0);
        const quantity = Math.max(1, Number(product.quantity) || 1);
        const productId = Number(product.productId || product.id) || null;
        const title = product.title || this.translate('cart_drawer_v2.adding_item', 'Adding item');
        const compareAtPrice = Number(product.compareAtPrice || basePrice);
        const variantId = String(product.id || '');
        const baselineQuantity = (this.cart.items || [])
          .filter((line) => String(line.variant_id || line.id) === variantId && !String(line.key || '').startsWith('pending-'))
          .reduce((total, line) => total + Number(line.quantity || 0), 0);
        if (productId) this.productTitlesById.set(String(productId), title);
        if (product.id && compareAtPrice > 0) this.compareAtByVariant.set(String(product.id), compareAtPrice);
        if (productId && product.productType) this.productTypesById.set(String(productId), product.productType);
        if (product.sellingPlans?.length) this.planOptionsByVariant.set(variantId, product.sellingPlans);
        const plan = product.sellingPlans?.find((option) => String(option.id) === String(product.selling_plan));
        if (product.otpSalePercentage != null) {
          this.otpPricingByVariant.set(variantId, { price: basePrice, percentage: Number(product.otpSalePercentage) });
        }
        const unitPrice = product.selling_plan ? Number(plan?.price ?? basePrice)
          : this.optimisticOneTimePrice({ ...product, variant_id: product.id }, basePrice);
        return {
          key: `${token}-${index}`,
          id: Number(product.id),
          variant_id: Number(product.id),
          product_id: productId,
          product_type: product.productType || '',
          handle: product.handle || '',
          title,
          product_title: title,
          variant_title: product.variantTitle || '',
          url: product.url || '#',
          image: product.image || product.imageUrl || '',
          quantity,
          price: unitPrice,
          original_price: compareAtPrice,
          final_price: unitPrice,
          original_line_price: compareAtPrice * quantity,
          final_line_price: unitPrice * quantity,
          properties: product.properties || {},
          selling_plan: product.selling_plan || null,
          selling_plan_id: product.selling_plan || null,
          selling_plan_allocation: plan ? { selling_plan: { id: plan.id, name: plan.name }, price: plan.price, compare_at_price: plan.compareAtPrice } : null,
          baselineQuantity,
          interactivePendingAdd: product.interactivePendingAdd === true,
          awaitingCartAdd: product.interactivePendingAdd === true,
        };
      });
      this.optimisticAdds.set(token, items);
      this.prioritizeAddedItems(items);
      this.cart.items = [...items, ...(this.cart.items || [])];
      this.updateTotalsOptimistically();
      this.render();
      this.open();
      return token;
    }

    cartItemDisplayIdentity(item) {
      return JSON.stringify([
        String(item.variant_id || item.id),
        Boolean(this.linePlanId(item)),
        Object.entries(item.properties || {})
          .filter(([key]) => key !== 'Discount')
          .sort(([first], [second]) => first.localeCompare(second)),
      ]);
    }

    subscriptionLineIdentity(item) {
      const plans = this.planOptionsByVariant.get(String(item.variant_id || item.id)) || [];
      return window.SaludSubscriptionCartSync?.subscriptionLineIdentity(item, plans) || null;
    }

    cartItemsForDisplay(items) {
      const groups = new Map();
      const displayed = [];
      const activeKey = document.activeElement?.closest('[data-cart-item]')?.dataset.key;
      for (const item of items) {
        const identity = this.isBlockedPendingItem(item) ? null : this.subscriptionLineIdentity(item);
        const existing = identity ? groups.get(identity) : null;
        if (!existing) {
          const next = structuredClone(item);
          next.mergedKeys = [item.key];
          if (identity) groups.set(identity, next);
          displayed.push(next);
          continue;
        }
        existing.quantity = Number(existing.quantity || 0) + Number(item.quantity || 0);
        existing.mergedKeys.push(item.key);
        existing.final_line_price = Number(existing.final_line_price ?? 0)
          + Number(item.final_line_price ?? Number(item.final_price ?? item.price ?? 0) * Number(item.quantity || 0));
        existing.original_line_price = Number(existing.original_line_price ?? 0)
          + Number(item.original_line_price ?? this.getOriginalUnitPrice(item) * Number(item.quantity || 0));
        if (item.key === activeKey) existing.key = item.key;
      }
      return displayed;
    }

    async mergeSubscriptionCart(cart) {
      const sync = window.SaludSubscriptionCartSync;
      if (!sync?.mergeSubscriptionLines) return cart;
      const merged = await sync.mergeSubscriptionLines(cart, this.planOptionsByVariant);
      if (merged === cart) return cart;
      for (const item of cart.items || []) {
        const identity = this.subscriptionLineIdentity(item);
        if (!identity) continue;
        const next = (merged.items || []).find((line) => line.key === item.key)
          || (merged.items || []).find((line) => this.subscriptionLineIdentity(line) === identity);
        if (!next || next.key === item.key) continue;
        for (const [key, serverKey] of this.serverLineKeys) {
          if (serverKey === item.key) this.serverLineKeys.set(key, next.key);
        }
        this.serverLineKeys.set(item.key, next.key);
      }
      return merged;
    }

    prioritizeAddedItems(items) {
      const order = ++this.nextAddedItemOrder;
      for (const item of items) {
        if (!this.isGiftItem(item)) this.addedItemOrder.set(this.cartItemDisplayIdentity(item), order);
      }
    }

    scrollRecommendations(direction) {
      const track = this.querySelector('[data-cart-recommendations-track]');
      const card = track?.querySelector('[data-recommendation-card]:not([hidden])');
      if (!track || !card) return;
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    async addRecommendation(button) {
      const variantId = Number(button.dataset.variantId);
      if (!variantId || button.disabled) return;
      if (this.hasPendingRecommendation(variantId)) return;
      if ((this.cart.items || []).some((item) => String(item.product_id) === String(button.dataset.productId))) return;
      const plans = this.planOptionsByVariant.get(String(variantId)) || [];
      const plan = this.recommendationPlan(plans);
      if (!plan) return;
      const quantity = this.recommendationQuantity();

      const recommendationImage = button.closest('[data-recommendation-card]')?.querySelector('img');
      const image = recommendationImage?.currentSrc || button.dataset.productImage || '';
      if (image) this.productImagesByVariant.set(String(variantId), image);

      const product = {
        id: variantId,
        productId: Number(button.dataset.productId),
        title: button.dataset.productTitle || '',
        productType: button.dataset.productType || '',
        handle: button.dataset.productHandle || '',
        url: button.dataset.productUrl || '',
        image,
        price: Number(plan.price),
        compareAtPrice: Number(button.dataset.productComparePrice || button.dataset.productPrice || 0),
        quantity,
        selling_plan: plan.id,
        sellingPlans: plans,
        interactivePendingAdd: true,
      };
      const token = this.beginOptimisticAddProducts([product]);
      const addedItems = this.optimisticAdds.get(token) || [];
      addedItems.forEach((item) => { item.awaitingCartAdd = true; });
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        this.ensureCartUpdateBatch();
        const request = this.requestQueue.then(async () => {
          await this.cartUpdateBatchReady;
          if (!this.optimisticAdds.has(token)) return;
          if (await this.commitCartChanges({ includePendingAdds: true })) return;
          const response = await fetch(this.dataset.cartAddUrl.endsWith('.js') ? this.dataset.cartAddUrl : `${this.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Salud-Cart-Sync': 'true' },
            body: JSON.stringify({ items: [{ id: variantId, quantity, selling_plan: plan.id }] }),
          });
          const data = await response.json();
          if (!response.ok || data.status) {
            throw new Error(data.description || this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
          }
          for (const item of addedItems) {
            item.awaitingCartAdd = false;
            const confirmed = (data.items || []).find((line) => this.matchesCartLine(line, item));
            if (confirmed) this.serverLineKeys.set(item.key, confirmed.key);
          }
          // Bind queued edits to the real line while keeping their latest local values.
          await this.refreshFromShopify(true);
        });
        this.requestQueue = request.catch(() => {});
        // Queue tier/frequency changes after the add; render them immediately.
        this.syncSubscriptionDelivery();
        this.scheduleCartUpdateBatchEnd();
        await request;
        this.announce(this.translate('cart_drawer_v2.item_added', 'Item added to your bag'));
      } catch (error) {
        this.failedCartUpdates += 1;
        this.rollbackOptimisticAdd(token);
        this.announce(error.message || this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
      } finally {
        button.disabled = this.checkoutPending === true || this.hasPendingRecommendation(variantId);
        button.removeAttribute('aria-busy');
      }
    }

    productFromForm(productForm, variantId) {
      if (!productForm) return null;
      let productData = null;
      try {
        productData = JSON.parse(productForm.dataset.cartProductVariants || 'null');
      } catch (error) {
        productData = null;
      }

      const form = productForm.querySelector('form');
      const planPicker = [...document.querySelectorAll('skio-plan-picker')].find((picker) =>
        form && (picker.formId === form.id || (!picker.formId && picker.closest('form') === form))
      );
      const pickerVariant = planPicker?.product?.variants?.find((entry) => String(entry.id) === variantId);
      const variant = productData?.variants?.find((entry) => String(entry.id) === variantId) || pickerVariant || {};
      const formSellingPlans = this.readJson({ textContent: productForm.dataset.cartSellingPlans }, []);
      const plansById = new Map([
        ...(productData?.selling_plan_groups || []),
        ...(pickerVariant ? planPicker.product.selling_plan_groups || [] : []),
      ].flatMap((group) => group.selling_plans || []).map((plan) => [String(plan.id), plan]));
      const allocations = variant.selling_plan_allocations?.length
        ? variant.selling_plan_allocations
        : pickerVariant?.selling_plan_allocations || [];
      const allocationPlans = allocations.map((allocation) => {
        const id = allocation.selling_plan?.id || allocation.selling_plan_id;
        const plan = allocation.selling_plan || plansById.get(String(id));
        return {
          id,
          name: plan?.name || allocation.name || '',
          options: plan?.options || allocation.options || [],
          price: Number(allocation.price) || 0,
          compareAtPrice: Number(allocation.compare_at_price) || Number(variant.price) || 0,
        };
      }).filter((plan) => plan.id);
      const fallbackImage = productForm.dataset.cartImage || '';
      const compareAtPrice = Number(variant.compare_at_price ?? productForm.dataset.cartComparePrice) || 0;
      return {
        productId: productData?.id || Number(productForm.dataset.cartProductId) || null,
        title: productData?.title || productForm.dataset.cartTitle || this.translate('cart_drawer_v2.adding_item', 'Adding item'),
        type: productData?.type || productForm.dataset.cartProductType || (pickerVariant ? planPicker.product.type : '') || '',
        handle: productData?.handle || productForm.dataset.cartHandle || '',
        url: productData?.url || productForm.dataset.cartUrl || (productForm.dataset.cartHandle ? `/products/${productForm.dataset.cartHandle}` : '#'),
        image: variant.image || variant.featured_image?.src || fallbackImage,
        variantTitle: variant.title && variant.title !== 'Default Title' ? variant.title : productForm.dataset.cartVariantTitle || '',
        price: Number(variant.price ?? productForm.dataset.cartPrice) || 0,
        otpSalePercentage: Number(productForm.dataset.otpSalePercentage) || 0,
        compareAtPrice,
        sellingPlans: allocationPlans.length ? allocationPlans
          : formSellingPlans.length ? formSellingPlans
            : this.planOptionsByVariant.get(variantId) || [],
      };
    }

    rollbackOptimisticAdd(token) {
      if (!token) return;
      const added = this.optimisticAdds.get(token);
      this.optimisticAdds.delete(token);
      const keys = new Set(Array.isArray(added) ? added.map((item) => item.key) : [added?.key]);
      for (const key of keys) {
        this.pendingMutations.delete(key);
        this.serverLineKeys.delete(key);
        this.manualDeliverySelections.delete(key);
      }
      this.cart.items = (this.cart.items || []).filter((item) => !keys.has(item.key));
      if ((Array.isArray(added) ? added : [added]).some((item) => item?.interactivePendingAdd)) this.syncSubscriptionDelivery();
      else {
        this.updateTotalsOptimistically();
        this.render();
      }
      this.refreshFromShopify();
    }

    renderContents() {
      this.refreshFromShopify();
      this.open();
    }

    async refreshFromShopify(rebasePendingChanges = false) {
      const responseVersion = this.cartResponseVersion;
      const queue = this.requestQueue;
      try {
        const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Unable to refresh the cart.');
        const cart = await response.json();
        if (!rebasePendingChanges && (responseVersion !== this.cartResponseVersion || queue !== this.requestQueue)) return;
        if (rebasePendingChanges) this.rebasePendingCartChanges(cart);
        this.setCart(cart);
      } catch (error) {
        this.announce(error.message || this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
      }
    }

    setCart(cart, { preserveUI = false } = {}) {
      this.cartResponseVersion += 1;
      this.serverCart = structuredClone(cart);
      if (this.cartStateElement) this.cartStateElement.textContent = JSON.stringify(cart);
      if (preserveUI) return;
      this.cart = structuredClone(cart);
      for (const item of this.cart.items || []) {
        const key = String(item.key || '');
        const hasServerAllocation = Object.prototype.hasOwnProperty.call(item, 'selling_plan_allocation');
        const planId = item.selling_plan_allocation?.selling_plan?.id
          || item.selling_plan_allocation?.selling_plan_id
          || item.selling_plan_id
          || (!hasServerAllocation ? this.sellingPlansByLineKey.get(key) : null);
        if (planId) {
          item.selling_plan_id = planId;
          this.sellingPlansByLineKey.set(key, String(planId));
        } else {
          this.sellingPlansByLineKey.delete(key);
        }
      }
      const serverVariantIds = new Set((cart.items || []).map((item) => String(item.variant_id || item.id)));
      const serverQuantities = (cart.items || []).reduce((quantities, item) => {
        const variantId = String(item.variant_id || item.id);
        quantities.set(variantId, (quantities.get(variantId) || 0) + Number(item.quantity || 0));
        return quantities;
      }, new Map());
      for (const [token, optimistic] of this.optimisticAdds.entries()) {
        const optimisticItems = Array.isArray(optimistic) ? optimistic : [optimistic];
        if (optimisticItems.some((item) => item.awaitingCartAdd)) continue;
        const confirmedItems = optimisticItems.filter((item) => serverVariantIds.has(String(item.variant_id || item.id)) && Number(serverQuantities.get(String(item.variant_id || item.id)) || 0) > Number(item.baselineQuantity || 0));
        if (confirmedItems.length) {
          const confirmedKeys = new Set(confirmedItems.map((item) => item.key));
          this.cart.items = (this.cart.items || []).filter((item) => !confirmedKeys.has(item.key));
          if (confirmedItems.length === optimisticItems.length) this.optimisticAdds.delete(token);
          else this.optimisticAdds.set(token, optimisticItems.filter((item) => !confirmedKeys.has(item.key)));
        }
      }
      const stillPendingAdds = [...this.optimisticAdds.values()].flatMap((items) => Array.isArray(items) ? items : [items]);
      if (stillPendingAdds.length) {
        this.cart.items = [...stillPendingAdds, ...(this.cart.items || [])];
        this.cart.item_count = Number(this.cart.item_count || 0) + stillPendingAdds.reduce((total, item) => total + Number(item.quantity || 0), 0);
        this.cart.total_price = Number(this.cart.total_price || 0) + stillPendingAdds.reduce((total, item) => total + Number(item.final_line_price || 0), 0);
      }
      this.reapplyPendingMutations();
      this.restoreManualDeliverySelections();
      if (!this.enforceMinimumSubscriptionQuantity()) this.render();
      if (!this.pendingMutations.size) this.serverLineKeys.clear();
    }

    reapplyPendingMutations() {
      if (!this.pendingMutations.size) return;
      for (const mutation of this.pendingMutations.values()) {
        const serverKey = this.serverLineKeys.get(mutation.key) || mutation.key;
        if (mutation.mergeKeeperKey
          && serverKey === (this.serverLineKeys.get(mutation.mergeKeeperKey) || mutation.mergeKeeperKey)) continue;
        const item = this.cart.items.find((line) => line.key === serverKey)
          || this.cart.items.find((line) => this.matchesCartLine(line, mutation.sourceItem));
        if (mutation.quantity <= 0) {
          if (item) this.cart.items = this.cart.items.filter((line) => line !== item);
          continue;
        }
        if (!item) continue;
        item.key = mutation.key;
        item.quantity = mutation.quantity;
        if (mutation.setSellingPlan) {
          const plan = mutation.sellingPlan
            ? (this.planOptionsByVariant.get(String(mutation.variantId)) || []).find((option) => String(option.id) === String(mutation.sellingPlan))
            : null;
          item.selling_plan = mutation.sellingPlan || null;
          item.selling_plan_id = mutation.sellingPlan || null;
          item.selling_plan_allocation = plan ? { selling_plan: { id: plan.id, name: plan.name }, price: plan.price, compare_at_price: plan.compareAtPrice } : null;
          item.final_price = mutation.sellingPlan ? plan?.price ?? item.final_price ?? item.price
            : this.optimisticOneTimePrice(item, this.otpPricingByVariant.get(String(mutation.variantId))?.price ?? item.original_price);
        }
        if (mutation.sellingPlan) this.sellingPlansByLineKey.set(String(item.key || ''), String(mutation.sellingPlan));
        item.final_line_price = Number(item.final_price ?? item.price ?? 0) * mutation.quantity;
        item.original_line_price = Number(item.original_price || item.price || 0) * mutation.quantity;
      }
      this.updateTotalsOptimistically();
    }

    async changeQuantity(item, quantity) {
      if (this.isBlockedPendingItem(item)) return;
      this.minimumCorrectionFailed = false;
      const keys = new Set(item.mergedKeys || [item.key]);
      const sourceItems = this.cart.items.filter((line) => keys.has(line.key));
      const sourceItem = sourceItems.find((line) => line.key === item.key);
      if (!sourceItem) return;
      if (this.linePlanId(sourceItem) && quantity > 0) {
        const groupQuantity = sourceItems.reduce((total, line) => total + Number(line.quantity || 0), 0);
        const otherQuantity = this.getSubscriptionPackQuantity() - groupQuantity;
        quantity = Math.max(quantity, this.minimumSubscriptionQuantity() - otherQuantity);
      }
      const nextCart = structuredClone(this.cart);
      const nextItem = nextCart.items.find((line) => line.key === item.key);
      if (!nextItem) return;

      this.manualDeliverySelections.clear();
      nextCart.items = nextCart.items.filter((line) => !keys.has(line.key) || line.key === item.key);
      if (quantity <= 0) {
        nextCart.items = nextCart.items.filter((line) => line.key !== item.key);
      } else {
        nextItem.quantity = quantity;
        nextItem.final_line_price = Number(nextItem.final_price ?? nextItem.price ?? 0) * quantity;
        nextItem.original_line_price = Number(nextItem.original_price || nextItem.price || 0) * quantity;
      }
      const minimumAdjustment = this.minimumSubscriptionAdjustment(nextCart);
      this.cart = nextCart;
      if (minimumAdjustment) this.enqueueChange(minimumAdjustment);
      for (const source of sourceItems) {
        if (source.key !== item.key) this.enqueueChange({
          key: source.key,
          variantId: source.variant_id,
          quantity: 0,
          sourceItem: source,
          mergeKeeperKey: item.key,
        });
      }
      this.enqueueChange({ key: item.key, variantId: item.variant_id, quantity, sourceItem });
      this.syncSubscriptionDelivery();
    }

    syncSubscriptionDelivery({ preserveFrequency = false } = {}) {
      const offer = this.offerForQuantity(this.getPackQuantity());
      const unavailableKeys = [];
      if (offer) {
        for (const item of [...this.cart.items]) {
          const currentPlanId = this.linePlanId(item);
          if (this.isGiftItem(item) || !currentPlanId || this.isBlockedPendingItem(item)) continue;
          const plans = this.planOptionsByVariant.get(String(item.variant_id || item.id)) || [];
          const manualSelection = this.manualDeliverySelectionForItem(item);
          const currentPlan = plans.find((plan) => String(plan.id) === String(currentPlanId))
            || item.selling_plan_allocation?.selling_plan;
          const preferredFrequency = this.getPlanFrequency(manualSelection?.plan)
            || (preserveFrequency ? this.getPlanFrequency(currentPlan) : '');
          const targetPlan = this.selectPlan(plans, offer, preferredFrequency)
            || (!preserveFrequency ? this.plansForOffer(plans, offer)
              .filter((plan) => this.getPlanFrequency(plan))
              .sort((first, second) => Number(this.getPlanFrequency(first)) - Number(this.getPlanFrequency(second)))[0] : null);
          if (!targetPlan) {
            unavailableKeys.push(item.key);
            continue;
          }
          if (manualSelection) manualSelection.plan = targetPlan;
          if (String(targetPlan.id) !== String(currentPlanId)) this.applySellingPlan(item, targetPlan, false);
        }
      }
      this.updateTotalsOptimistically();
      this.render();
      for (const key of unavailableKeys) {
        this.showItemError(key, this.translate('cart_drawer_v2.plan_unavailable', 'Subscription options are unavailable for this item.'));
      }
    }

    async subscribeItem(item) {
      if (this.isBlockedPendingItem(item)) return;
      if (this.getSubscriptionPackQuantity() + Number(item.quantity || 0) < this.minimumSubscriptionQuantity()) return;
      const plans = this.planOptionsByVariant.get(String(item.variant_id)) || [];
      const currentPlanId = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan_id || null;
      if (currentPlanId) return;
      const offer = this.offerForQuantity(this.getSubscriptionPackQuantity() + Number(item.quantity || 0));
      const targetPlan = this.selectPlan(plans, offer);
      if (!targetPlan) {
        this.showItemError(item.key, this.translate('cart_drawer_v2.plan_unavailable', 'Subscription options are unavailable for this item.'));
        return;
      }

      this.applySellingPlan(item, targetPlan, false);
      this.syncSubscriptionDelivery();
    }

    async changeFrequency(item, sellingPlanId) {
      if (this.isBlockedPendingItem(item)) return;
      const plans = this.planOptionsByVariant.get(String(item.variant_id)) || [];
      const selectedPlan = plans.find((plan) => String(plan.id) === String(sellingPlanId));
      if (!selectedPlan) return;
      const offer = this.offerForQuantity(this.getSubscriptionPackQuantity());
      const targetPlan = offer
        ? this.selectPlan(plans, offer, this.getPlanFrequency(selectedPlan))
        : selectedPlan;
      if (!targetPlan) {
        this.showItemError(item.key, this.translate('cart_drawer_v2.plan_unavailable', 'Subscription options are unavailable for this item.'));
        return;
      }
      const keys = new Set(item.mergedKeys || [item.key]);
      const sourceItems = this.cart.items.filter((line) => keys.has(line.key));
      for (const source of sourceItems) {
        const previousSelection = this.manualDeliverySelectionForItem(source);
        if (previousSelection) this.manualDeliverySelections.delete(previousSelection.key);
        this.manualDeliverySelections.set(source.key, {
          key: source.key,
          sourceItem: structuredClone(source),
          plan: targetPlan,
          quantitySignature: this.deliveryQuantitySignature(),
        });
        if (String(this.linePlanId(source)) !== String(targetPlan.id)) this.applySellingPlan(source, targetPlan, false);
      }
      this.syncSubscriptionDelivery({ preserveFrequency: true });
    }

    deliveryQuantitySignature() {
      const quantities = new Map();
      for (const item of this.cart.items || []) {
        if (this.isGiftItem(item)) continue;
        const variantId = String(item.variant_id || item.id);
        if (!this.linePlanId(item) && !(this.planOptionsByVariant.get(variantId) || []).length) continue;
        const identity = JSON.stringify([variantId, Object.entries(item.properties || {})
          .filter(([key]) => key !== 'Discount')
          .sort(([first], [second]) => first.localeCompare(second))]);
        quantities.set(identity, (quantities.get(identity) || 0) + Number(item.quantity || 0));
      }
      return JSON.stringify([...quantities].sort(([first], [second]) => first.localeCompare(second)));
    }

    manualDeliverySelectionForItem(item) {
      const quantitySignature = this.deliveryQuantitySignature();
      return [...this.manualDeliverySelections.values()].find((selection) => {
        if (selection.quantitySignature !== quantitySignature) return false;
        return item.key === selection.key
          || item.key === this.serverLineKeys.get(selection.key)
          || this.matchesCartLine(item, selection.sourceItem, selection.plan.id)
          || this.matchesCartLine(item, selection.sourceItem)
          || (String(this.linePlanId(item)) === String(selection.plan.id)
            && this.cartItemDisplayIdentity(item) === this.cartItemDisplayIdentity(selection.sourceItem));
      });
    }

    restoreManualDeliverySelections() {
      if (!this.manualDeliverySelections.size) return;
      let changed = false;
      for (const item of [...this.cart.items]) {
        if (this.isGiftItem(item) || !this.linePlanId(item)) continue;
        const selection = this.manualDeliverySelectionForItem(item);
        if (selection && String(this.linePlanId(item)) !== String(selection.plan.id)) {
          this.applySellingPlan(item, selection.plan, false);
          changed = true;
        }
      }
      if (changed) this.updateTotalsOptimistically();
    }

    applySellingPlan(item, targetPlan, render = true) {
      const nextCart = structuredClone(this.cart);
      const nextItem = nextCart.items.find((line) => line.key === item.key);
      if (!nextItem) return;
      nextItem.selling_plan = targetPlan.id;
      nextItem.selling_plan_id = targetPlan.id;
      this.sellingPlansByLineKey.set(String(nextItem.key || ''), String(targetPlan.id));
      nextItem.selling_plan_allocation = {
        selling_plan: { id: targetPlan.id, name: targetPlan.name },
        price: targetPlan.price,
        compare_at_price: targetPlan.compareAtPrice,
      };
      nextItem.final_price = targetPlan.price ?? nextItem.final_price;
      nextItem.final_line_price = Number(nextItem.final_price || 0) * Number(nextItem.quantity || 1);
      this.cart = nextCart;
      if (render) {
        this.updateTotalsOptimistically();
        this.render();
      }
      this.enqueueChange({
        key: item.key,
        variantId: item.variant_id,
        quantity: item.quantity,
        sellingPlan: targetPlan.id,
        setSellingPlan: true,
        sourceItem: item,
      });
    }

    getPackQuantity(items = this.cart.items || []) {
      return this.getSubscriptionPackQuantity(items);
    }

    getSubscriptionPackQuantity(items = this.cart.items || []) {
      return items.reduce((total, item) => {
        if (this.isGiftItem(item)) return total;
        return this.linePlanId(item) ? total + Number(item.quantity || 0) : total;
      }, 0);
    }

    minimumSubscriptionQuantity() {
      return Math.max(1, Number(this.dataset.minimumSubscriptionQuantity
        || window.SaludSubscriptionConfig?.minimumSubscriptionQuantity) || 1);
    }

    minimumSubscriptionAdjustment(cart) {
      const quantity = this.getSubscriptionPackQuantity(cart.items || []);
      const minimum = this.minimumSubscriptionQuantity();
      if (quantity === 0 || quantity >= minimum) return null;
      const item = (cart.items || []).find((line) => !this.isGiftItem(line)
        && this.linePlanId(line) && !this.isBlockedPendingItem(line));
      if (!item) return null;
      const sourceItem = structuredClone(item);
      item.quantity = Number(item.quantity || 0) + minimum - quantity;
      item.final_line_price = Number(item.final_price ?? item.price ?? 0) * item.quantity;
      item.original_line_price = this.getOriginalUnitPrice(item) * item.quantity;
      return { key: item.key, variantId: item.variant_id || item.id, quantity: item.quantity, sourceItem, minimumCorrection: true };
    }

    enforceMinimumSubscriptionQuantity() {
      if (this.minimumCorrectionFailed || this.pendingMutations.size || this.optimisticAdds.size) return false;
      const adjustment = this.minimumSubscriptionAdjustment(this.cart);
      if (!adjustment) return false;
      this.manualDeliverySelections.clear();
      this.enqueueChange(adjustment);
      this.syncSubscriptionDelivery();
      return true;
    }

    canDecreaseItem(item) {
      return Number(item.quantity) > 1 && (!this.linePlanId(item)
        || this.getSubscriptionPackQuantity() > this.minimumSubscriptionQuantity());
    }

    offerForQuantity(quantity) {
      return [...this.offers]
        .filter((offer) => Number(offer.quantity) <= Number(quantity))
        .sort((first, second) => Number(second.quantity) - Number(first.quantity))[0] || null;
    }

    giftOfferForQuantity(quantity) {
      return [...this.offers]
        .filter((offer) => Number(offer.quantity) <= Number(quantity))
        .sort((first, second) => Number(second.quantity) - Number(first.quantity))[0] || null;
    }

    getPlanFrequency(plan) {
      const values = [...(plan?.options || []).map((option) => typeof option === 'string' ? option : option?.value || ''), plan?.name || ''];
      for (const value of values) {
        const match = String(value).match(/(\d+)\s*days?\b/i);
        if (match) return String(match[1]);
      }
      return '';
    }

    getPlanLabel(plan) {
      const frequency = this.getPlanFrequency(plan);
      if (frequency) {
        return this.translate('cart_drawer_v2.delivery_every', 'Delivery every {{ days }} days').replace('{{ days }}', frequency);
      }
      const label = (plan.options || [])
        .map((option) => typeof option === 'string' ? option : option?.value || '')
        .find((option) => /\d+\s*days?/i.test(String(option)));
      return label || plan.name || '';
    }

    planDiscount(plan) {
      const compareAt = Number(plan.compareAtPrice || 0);
      const price = Number(plan.price || 0);
      return compareAt > 0 ? Math.round((compareAt - price) / compareAt * 100) : null;
    }

    subscriptionDiscountProperties(item, planId = this.linePlanId(item)) {
      if (!planId || this.isGiftItem(item)) return null;
      const plans = this.planOptionsByVariant.get(String(item.variant_id || item.id)) || [];
      const allocation = item.selling_plan_allocation;
      const plan = plans.find((option) => String(option.id) === String(planId))
        || (String(this.linePlanId(item)) === String(planId) && allocation ? {
          price: allocation.price,
          compareAtPrice: allocation.compare_at_price || this.getOriginalUnitPrice(item),
        } : null);
      const discount = plan ? this.planDiscount(plan) : null;
      return discount === null ? null : { ...item.properties, Discount: `${discount}%` };
    }

    syncSubscriptionDiscountProperties() {
      if (this.pendingMutations.size || this.optimisticAdds.size) return;
      for (const item of this.cart.items || []) {
        if (this.isBlockedPendingItem(item)) continue;
        const properties = this.subscriptionDiscountProperties(item);
        if (!properties || properties.Discount === item.properties?.Discount) continue;
        this.enqueueChange({
          key: item.key,
          variantId: item.variant_id || item.id,
          quantity: item.quantity,
          sourceItem: structuredClone(item),
        });
      }
    }

    plansForOffer(plans, offer) {
      const discount = Number(offer?.orderDiscount);
      return plans.filter((plan) => (!Number.isFinite(discount) || this.planDiscount(plan) === discount)
        && window.SaludSubscriptionConfig.isFrequencyAllowed(this.getPlanFrequency(plan), offer));
    }

    selectPlan(plans, offer, preferredFrequency = '') {
      if (!plans?.length) return null;
      const targetDiscount = Number(offer?.orderDiscount);
      const targetFrequencyValue = String(preferredFrequency || offer?.defaultFrequency || '');
      const targetFrequency = targetFrequencyValue.match(/\d+/)?.[0] || targetFrequencyValue;
      if (!Number.isFinite(targetDiscount)) return null;
      const allowedPlans = this.plansForOffer(plans, offer);
      const nearestFrequency = window.SaludSubscriptionConfig.nearestFrequency(
        allowedPlans.map((plan) => this.getPlanFrequency(plan)), targetFrequency,
      );
      return allowedPlans.find((plan) => this.getPlanFrequency(plan) === nearestFrequency) || null;
    }

    linePlanId(item) {
      return item?.selling_plan_allocation?.selling_plan?.id
        || item?.selling_plan_allocation?.selling_plan_id
        || item?.selling_plan_id
        || (typeof item?.selling_plan === 'object' ? item.selling_plan?.id : item?.selling_plan)
        || null;
    }

    matchesCartLine(item, source, planId = this.linePlanId(source)) {
      if (!source || String(item.variant_id || item.id) !== String(source.variant_id || source.id)) return false;
      const properties = (line) => JSON.stringify(Object.entries(line.properties || {}).sort(([first], [second]) => first.localeCompare(second)));
      return String(this.linePlanId(item) || '') === String(planId || '') && properties(item) === properties(source);
    }

    ensureCartUpdateBatch() {
      if (this.cartUpdateBatchSync) return;
      const sync = window.SaludSubscriptionCartSync;
      if (typeof sync?.pauseCartSync !== 'function') return;
      this.cartUpdateBatchSync = sync;
      this.cartUpdateBatchReady = sync.pauseCartSync().then(async (hadRunningSync) => {
        if (hadRunningSync) await this.refreshFromShopify(true);
      });
    }

    rebasePendingCartChanges(cart) {
      const properties = (item) => JSON.stringify(Object.entries(item?.properties || {})
        .filter(([key]) => key !== 'Discount')
        .sort(([first], [second]) => first.localeCompare(second)));
      for (const mutation of this.pendingMutations.values()) {
        const source = mutation.sourceItem;
        if (!source) continue;
        const plans = this.planOptionsByVariant.get(String(mutation.variantId)) || [];
        const sourcePlan = plans.find((plan) => String(plan.id) === String(this.linePlanId(source)));
        const item = (cart.items || []).find((line) => line.key === mutation.key || this.matchesCartLine(line, source))
          || (cart.items || []).find((line) => {
            if (String(line.variant_id || line.id) !== String(mutation.variantId) || properties(line) !== properties(source)) return false;
            const plan = plans.find((candidate) => String(candidate.id) === String(this.linePlanId(line)));
            return this.linePlanId(line) === this.linePlanId(source)
              || (sourcePlan && plan && this.getPlanFrequency(plan) === this.getPlanFrequency(sourcePlan));
          });
        if (item) {
          this.serverLineKeys.set(mutation.key, item.key);
          mutation.sourceItem = structuredClone(item);
        }
      }
    }

    finishCartUpdateBatch() {
      const sync = this.cartUpdateBatchSync;
      this.cartUpdateBatchSync = null;
      sync?.resumeCartSync(this.serverCart);
    }

    scheduleCartUpdateBatchEnd() {
      const queue = this.requestQueue;
      queue.then(() => {
        if (queue === this.requestQueue && !this.pendingMutations.size && !this.checkoutPending) this.finishCartUpdateBatch();
      });
    }

    scheduleCartChangeDelay() {
      if (this.checkoutPending) return;
      if (!this.cartChangeDelay) {
        this.cartChangeDelay = new Promise((resolve) => { this.resolveCartChangeDelay = resolve; });
      }
      clearTimeout(this.cartChangeDelayTimer);
      this.cartChangeDelayTimer = setTimeout(() => this.flushCartChangeDelay(), 120);
    }

    flushCartChangeDelay() {
      clearTimeout(this.cartChangeDelayTimer);
      this.cartChangeDelay = null;
      this.resolveCartChangeDelay?.();
      this.resolveCartChangeDelay = null;
    }

    setCheckoutLoading(pending) {
      this.checkoutPending = pending;
      if (!pending) this.preparedCheckoutCart = null;
      if (this.checkoutElement) {
        this.checkoutElement.disabled = pending;
        this.checkoutElement.setAttribute('aria-busy', String(pending));
        this.checkoutElement.setAttribute('aria-disabled', String(pending));
      }
      this.render();
    }

    coalescePendingSubscriptionChanges() {
      const groups = new Map();
      for (const mutation of this.pendingMutations.values()) {
        if (!mutation.setSellingPlan || !mutation.sellingPlan || mutation.quantity <= 0 || mutation.mergeKeeperKey) continue;
        const serverKey = this.serverLineKeys.get(mutation.key) || mutation.key;
        if (!(this.serverCart.items || []).some((item) => item.key === serverKey)) continue;
        const identity = JSON.stringify([serverKey, String(mutation.sellingPlan)]);
        const previous = groups.get(identity);
        if (!previous) groups.set(identity, mutation);
        else {
          previous.quantity += Number(mutation.quantity);
          this.pendingMutations.delete(mutation.key);
        }
      }
    }

    async commitCartChanges({ includePendingAdds = false } = {}) {
      if ((!this.pendingMutations.size && !this.optimisticAdds.size)
        || (!includePendingAdds && this.optimisticAdds.size)) return false;
      const sync = window.SaludSubscriptionCartSync;
      if (!sync?.giftStateForCart) return false;
      const mutationVersion = sync.externalCartMutationVersion;
      let state = sync.giftStateForCart(this.cart);
      if (sync.hasMatchingSubscriptionGifts(this.serverCart.items || [], state.giftLines, state.offer)) return false;
      await window.SaludSubscriptionConfig.refreshGiftInventory();
      const products = structuredClone(this.cart.items.filter((item) => !this.isGiftItem(item)));
      for (const item of products) {
        const properties = this.subscriptionDiscountProperties(item);
        if (properties) item.properties = properties;
      }
      state = sync.giftStateForCart({ items: products });
      const mutations = new Map(this.pendingMutations);
      const optimisticAdds = new Map(this.optimisticAdds);
      let cart = await this.prepareCheckoutGiftOrder(this.serverCart, state.giftLines, products);
      cart = await sync.normalizeSubscriptionPlans(cart);
      await sync.assertCheckoutReady(cart);
      for (const [key, mutation] of mutations) {
        if (this.pendingMutations.get(key) !== mutation) continue;
        this.pendingMutations.delete(key);
        this.serverLineKeys.delete(key);
      }
      for (const product of products) {
        const updated = cart.items.find((item) => this.matchesCartLine(item, product));
        if (!updated) continue;
        this.serverLineKeys.set(product.key, updated.key);
        const mutation = this.pendingMutations.get(product.key);
        if (mutation) mutation.sourceItem = structuredClone(updated);
      }
      for (const [token, added] of optimisticAdds) {
        if (this.optimisticAdds.get(token) !== added) continue;
        for (const item of Array.isArray(added) ? added : [added]) item.awaitingCartAdd = false;
      }
      this.setCart(cart);
      if (mutationVersion === sync.externalCartMutationVersion && !sync.pendingCartRequests.size) {
        this.preparedCheckoutCart = { cart, mutationVersion, responseVersion: this.cartResponseVersion };
      }
      this.announce(this.translate('cart_drawer_v2.cart_updated', 'Cart updated'));
      return true;
    }

    enqueueChange(change) {
      this.ensureCartUpdateBatch();
      this.scheduleCartChangeDelay();
      const previous = this.pendingMutations.get(change.key);
      const queuedChange = { ...previous, ...change, sourceItem: previous?.sourceItem || change.sourceItem };
      this.pendingMutations.set(change.key, queuedChange);
      this.requestQueue = this.requestQueue.then(async () => {
        await this.cartUpdateBatchReady;
        await this.cartChangeDelay;
        this.coalescePendingSubscriptionChanges();
        if (this.pendingMutations.get(change.key) !== queuedChange) return;
        try {
          if (await this.commitCartChanges()) return;
          const serverKey = this.serverLineKeys.get(change.key) || change.key;
          const serverItems = this.serverCart.items || [];
          const lineIndex = serverItems.findIndex((line) => line.key === serverKey);
          const keeperKey = queuedChange.mergeKeeperKey
            ? this.serverLineKeys.get(queuedChange.mergeKeeperKey) || queuedChange.mergeKeeperKey
            : null;
          if (keeperKey && (serverKey === keeperKey || lineIndex < 0)) {
            this.pendingMutations.delete(change.key);
            this.setCart(this.serverCart, { preserveUI: this.pendingMutations.size > 0 });
            return;
          }
          const sourceIdentity = this.subscriptionLineIdentity(queuedChange.sourceItem);
          const resolvedIndex = lineIndex >= 0 ? lineIndex : serverItems.findIndex((line) =>
            this.matchesCartLine(line, queuedChange.sourceItem)
            || (sourceIdentity && this.subscriptionLineIdentity(line) === sourceIdentity));
          if (resolvedIndex < 0) {
            if (queuedChange.quantity <= 0) {
              this.pendingMutations.delete(change.key);
              this.setCart(this.serverCart, { preserveUI: this.pendingMutations.size > 0 });
              return;
            }
            throw new Error(this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
          }
          const serverItem = serverItems[resolvedIndex];
          const payload = { quantity: queuedChange.quantity };
          if (queuedChange.setSellingPlan && queuedChange.quantity > 0) {
            payload.line = resolvedIndex + 1;
            payload.selling_plan = queuedChange.sellingPlan;
          } else {
            payload.id = serverItem.key;
          }
          const planId = payload.selling_plan === undefined ? this.linePlanId(serverItem) : payload.selling_plan;
          if (queuedChange.quantity > 0) {
            const properties = this.subscriptionDiscountProperties(serverItem, planId);
            if (properties && properties.Discount !== serverItem.properties?.Discount) payload.properties = properties;
          }
          const response = await fetch(this.dataset.cartChangeUrl.endsWith('.js') ? this.dataset.cartChangeUrl : `${this.dataset.cartChangeUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Salud-Cart-Sync': 'true' },
            body: JSON.stringify(payload),
          });
          let cart = await response.json();
          if (!response.ok || cart.status) throw new Error(cart.description || this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
          const expectedItem = payload.properties ? { ...serverItem, properties: payload.properties } : serverItem;
          const updatedItem = cart.items.find((line) => this.matchesCartLine(line, expectedItem, planId));
          if (updatedItem) this.serverLineKeys.set(change.key, updatedItem.key);
          const latest = this.pendingMutations.get(change.key);
          if (latest === queuedChange) {
            this.pendingMutations.delete(change.key);
          } else if (latest) {
            if (updatedItem) {
              latest.sourceItem = structuredClone(updatedItem);
            }
          }
          this.serverCart = structuredClone(cart);
          if (!this.pendingMutations.size) cart = await this.mergeSubscriptionCart(cart);
          this.setCart(cart, { preserveUI: this.pendingMutations.size > 0 });
          this.announce(this.translate('cart_drawer_v2.cart_updated', 'Cart updated'));
        } catch (error) {
          this.failedCartUpdates += 1;
          if (queuedChange.minimumCorrection) this.minimumCorrectionFailed = true;
          this.manualDeliverySelections.clear();
          if (this.pendingMutations.get(change.key) === queuedChange) {
            this.pendingMutations.delete(change.key);
            this.serverLineKeys.delete(change.key);
          }
          this.setCart(this.serverCart);
          this.announce(error.message || this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
          await this.refreshFromShopify();
        }
      });
      this.scheduleCartUpdateBatchEnd();
    }

    async completeCheckout() {
      if (this.checkoutPending) return;
      const checkoutUrl = this.checkoutElement?.dataset.checkoutUrl || `${window.Shopify?.routes?.root || '/'}checkout`;
      this.preparedCheckoutCart = null;
      this.minimumCorrectionFailed = false;
      this.enforceMinimumSubscriptionQuantity();
      this.setCheckoutLoading(true);
      this.ensureCartUpdateBatch();
      this.flushCartChangeDelay();
      let navigating = false;
      const failureCount = this.failedCartUpdates;
      try {
        await this.cartUpdateBatchReady;
        let queue;
        do {
          queue = this.requestQueue;
          await queue;
          if (this.failedCartUpdates !== failureCount) return;
          if (queue !== this.requestQueue) continue;
          if (this.pendingMutations.size > 0) {
            throw new Error(this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
          }
          this.syncSubscriptionDiscountProperties();
          if (queue !== this.requestQueue) continue;
          const giftSync = window.SaludSubscriptionCartSync;
          if (typeof giftSync?.prepareCheckout !== 'function') {
            throw new Error(this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
          }
          const prepared = this.preparedCheckoutCart;
          const usePreparedCart = prepared && prepared.responseVersion === this.cartResponseVersion
            && prepared.mutationVersion === giftSync.externalCartMutationVersion && !giftSync.pendingCartRequests.size;
          let cart = usePreparedCart
            ? await giftSync.assertCheckoutReady(prepared.cart)
            : await giftSync.prepareCheckout();
          if (usePreparedCart && (prepared.responseVersion !== this.cartResponseVersion
            || prepared.mutationVersion !== giftSync.externalCartMutationVersion || giftSync.pendingCartRequests.size)) {
            cart = await giftSync.prepareCheckout();
          }
          if (queue !== this.requestQueue) continue;
          if (!cart.items?.length) throw giftSync.giftError();
          const subscriptionQuantity = this.getSubscriptionPackQuantity(cart.items || []);
          if (subscriptionQuantity > 0 && subscriptionQuantity < this.minimumSubscriptionQuantity()) {
            this.setCart(cart);
            if (queue !== this.requestQueue) continue;
            throw new Error(this.translate('cart_drawer_v2.subscription_minimum', 'Subscriptions require at least {{ quantity }} packs.')
              .replace('{{ quantity }}', String(this.minimumSubscriptionQuantity())));
          }
          const orderedCart = await this.prepareCheckoutGiftOrder(cart);
          if (orderedCart !== cart) {
            cart = orderedCart;
            await giftSync.assertCheckoutReady(cart);
          }
          this.setCart(cart);
        } while (queue !== this.requestQueue);
        if (this.failedCartUpdates === failureCount && this.pendingMutations.size === 0) {
          if (typeof window.bndlr?.checkout === 'function') window.bndlr.checkout();
          else window.location.assign(checkoutUrl);
          navigating = true;
        }
      } catch (error) {
        this.announce(error.message || this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
      } finally {
        if (!navigating) {
          this.setCheckoutLoading(false);
          this.finishCartUpdateBatch();
        }
      }
    }

    checkoutGiftsAreLast(cart) {
      let giftSeen = false;
      for (const item of cart.items || []) {
        if (this.isGiftItem(item)) giftSeen = true;
        else if (giftSeen) return false;
      }
      return true;
    }

    checkoutLineQuantities(items) {
      const lines = new Map();
      for (const item of items || []) {
        const id = Number(item.variant_id || item.id);
        const sellingPlan = this.linePlanId(item);
        const properties = Object.fromEntries(Object.entries(item.properties || {}).sort(([first], [second]) => first.localeCompare(second)));
        const identity = JSON.stringify([String(id), String(sellingPlan || ''), properties]);
        const previous = lines.get(identity);
        if (previous) previous.quantity += Number(item.quantity || 0);
        else lines.set(identity, {
          id,
          quantity: Number(item.quantity || 0),
          properties,
          ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
        });
      }
      return lines;
    }

    async checkoutCartRequest(url, body) {
      const response = await fetch(url.endsWith('.js') ? url : `${url}.js`, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Salud-Cart-Sync': 'true' },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const cart = await response.json();
      if (!response.ok || cart.status || !Array.isArray(cart.items)) {
        throw new Error(cart.description || this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
      }
      return cart;
    }

    async restoreCheckoutProducts(products, cart) {
      const expected = this.checkoutLineQuantities(products);
      let lastError;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const actual = this.checkoutLineQuantities(cart.items);
        const missing = [...expected].flatMap(([identity, item]) => {
          const quantity = item.quantity - (actual.get(identity)?.quantity || 0);
          return quantity > 0 ? [{ ...item, quantity }] : [];
        });
        if (!missing.length) return cart;
        try {
          await this.checkoutCartRequest(this.dataset.cartAddUrl, { items: missing });
        } catch (error) {
          lastError = error;
        }
        cart = await this.checkoutCartRequest(`${this.dataset.cartUrl}.js`);
      }
      const actual = this.checkoutLineQuantities(cart.items);
      if ([...expected].every(([identity, item]) => actual.get(identity)?.quantity === item.quantity)) return cart;
      throw lastError || new Error(this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
    }

    async prepareCheckoutGiftOrder(cart, giftLines = null, replacementProducts = null) {
      if (giftLines === null && this.checkoutGiftsAreLast(cart)) return cart;
      const originalProducts = structuredClone(cart.items.filter((item) => !this.isGiftItem(item)));
      const products = replacementProducts || originalProducts;
      const replacedGifts = giftLines === null ? []
        : cart.items.filter((item) => window.SaludSubscriptionCartSync.isManagedSubscriptionGift(item));
      const replacedKeys = new Set(replacedGifts.map((item) => item.key));
      const expected = this.checkoutLineQuantities(giftLines === null ? cart.items
        : [...products, ...cart.items.filter((item) => this.isGiftItem(item) && !replacedKeys.has(item.key)), ...giftLines]);
      if ([...expected.values()].some((item) => !Number.isSafeInteger(item.id) || item.id <= 0)) {
        throw new Error(this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
      }
      try {
        const removedKeys = new Set([...originalProducts, ...replacedGifts].map((item) => item.key));
        cart = await this.checkoutCartRequest(this.dataset.cartUpdateUrl, {
          updates: cart.items.map((item) => removedKeys.has(item.key) ? 0 : item.quantity),
        });
        cart = await this.restoreCheckoutProducts(giftLines === null ? products : [...giftLines, ...products], cart);
        const actual = this.checkoutLineQuantities(cart.items);
        if (actual.size !== expected.size
          || ![...expected].every(([identity, item]) => actual.get(identity)?.quantity === item.quantity)
          || !this.checkoutGiftsAreLast(cart)) {
          throw new Error(this.translate('cart_drawer_v2.update_error', 'Your cart could not be updated.'));
        }
        return cart;
      } catch (error) {
        try {
          cart = await this.checkoutCartRequest(`${this.dataset.cartUrl}.js`);
          if (giftLines !== null) {
            const identities = this.checkoutLineQuantities([...originalProducts, ...products]);
            const updates = cart.items.map((item) => window.SaludSubscriptionCartSync.isManagedSubscriptionGift(item)
              || (!this.isGiftItem(item) && identities.has([...this.checkoutLineQuantities([item]).keys()][0])) ? 0 : item.quantity);
            if (updates.some((quantity, index) => quantity !== cart.items[index].quantity)) {
              cart = await this.checkoutCartRequest(this.dataset.cartUpdateUrl, { updates });
            }
          }
          cart = await this.restoreCheckoutProducts(originalProducts, cart);
          this.setCart(cart);
        } catch (restoreError) {
          await this.refreshFromShopify(true);
        }
        throw error;
      }
    }

    updateTotalsOptimistically() {
      const items = this.cart.items || [];
      this.cart.item_count = items.reduce((total, item) => total + Number(item.quantity || 0), 0);
      this.cart.total_price = items.reduce((total, item) => total + Number(item.final_line_price ?? Number(item.final_price ?? item.price ?? 0) * Number(item.quantity || 0)), 0);
      this.cart.items_subtotal_price = this.cart.total_price;
    }

    optimisticOneTimePrice(item, fallback) {
      if (this.linePlanId(item) || this.isGiftItem(item)) return Number(fallback);
      const pricing = this.otpPricingByVariant.get(String(item.variant_id || item.id));
      if (!pricing || !Number.isFinite(pricing.price)) return Number(fallback);
      return window.SaludOtpSale?.price(pricing.price, pricing.percentage) ?? Number(fallback);
    }

    getOriginalUnitPrice(item) {
      const variantId = String(item.variant_id || item.id || '');
      return Math.max(
        Number(item.original_price ?? item.price ?? 0),
        Number(item.selling_plan_allocation?.compare_at_price || 0),
        Number(this.compareAtByVariant.get(variantId) || 0),
      );
    }

    getCheckoutTotal() {
      return Math.max(0, Number(this.cart.total_price || 0));
    }

    getOriginalCartTotal() {
      return (this.cart.items || []).reduce((total, item) => {
        if (this.isGiftItem(item)) return total;
        const quantity = Number(item.quantity || 0);
        const finalLinePrice = Number(item.final_line_price ?? Number(item.final_price ?? item.price ?? 0) * quantity);
        return total + Math.max(finalLinePrice, Number(item.original_line_price || 0), this.getOriginalUnitPrice(item) * quantity);
      }, 0);
    }

    renderProgress() {
      if (!this.progressElement || !this.progressTrackElement) return;
      const offers = [...this.offers]
        .filter((offer) => (offer.showInCartDrawer === true || offer.displayOn === 'Bundle Builder only') && Number(offer.quantity) > 0)
        .sort((first, second) => Number(first.quantity) - Number(second.quantity))
        .filter((offer, index, all) => index === 0 || Number(offer.quantity) !== Number(all[index - 1].quantity));
      this.progressElement.hidden = false;
      const quantity = this.getSubscriptionPackQuantity();
      const completedIndex = offers.reduce((completed, offer, index) => quantity >= Number(offer.quantity) ? index : completed, -1);
      const reachedOffer = offers[completedIndex];
      const nextOffer = offers[completedIndex + 1];
      const text = String(nextOffer?.progressRemainingText || reachedOffer?.progressReachedText || '');
      const fill = offers.length ? ((completedIndex + 1) / offers.length) * 100 : 0;
      const maxQuantity = Math.max(1, Number(offers[offers.length - 1]?.quantity || 1));
      this.progressElement.style.setProperty('--cart-drawer-v2-progress', `${fill}%`);
      if (this.progressTextElement) {
        this.progressTextElement.textContent = text;
        this.progressTextElement.hidden = !text;
      }
      this.progressTrackElement.setAttribute('aria-valuemax', String(maxQuantity));
      this.progressTrackElement.setAttribute('aria-valuenow', String(Math.min(quantity, maxQuantity)));
      this.progressTrackElement.setAttribute('aria-valuetext', text || `${quantity} / ${maxQuantity}`);
      this.renderPacksProgress(offers, completedIndex, quantity, text, maxQuantity);
    }

    renderPacksProgress(offers, completedIndex, quantity, text, maxQuantity) {
      if (!this.packsProgressElement || !this.packsProgressTrackElement || !this.packsProgressStepsElement) return;
      const count = offers.length;
      const fill = completedIndex < 0 ? 0
        : completedIndex === count - 1 ? 100
          : ((completedIndex + 0.5) / count) * 100;
      this.packsProgressElement.style.setProperty('--cart-drawer-v2-packs-progress', `${fill}%`);
      this.packsProgressElement.style.setProperty('--cart-drawer-v2-packs-count', String(Math.max(1, count)));
      this.packsProgressTrackElement.setAttribute('aria-valuemax', String(maxQuantity));
      this.packsProgressTrackElement.setAttribute('aria-valuenow', String(Math.min(quantity, maxQuantity)));
      this.packsProgressTrackElement.setAttribute('aria-valuetext', text || `${quantity} / ${maxQuantity}`);
      const markup = offers.map((offer, index) => {
        const reached = index <= completedIndex;
        const badge = String(offer.badgeText || '');
        return `<li class="cart-drawer-v2__packs-progress-step${reached ? ' is-active' : ''}" data-pack-quantity="${this.escapeHtml(offer.quantity)}"${index === completedIndex ? ' aria-current="step"' : ''}>
          <span class="cart-drawer-v2__packs-progress-badge${badge ? '' : ' is-empty'}">${this.escapeHtml(badge)}</span>
          <span class="cart-drawer-v2__packs-progress-point" aria-hidden="true"></span>
          <span class="cart-drawer-v2__packs-progress-label">${this.escapeHtml(offer.title || '')}</span>
        </li>`;
      }).join('');
      if (markup !== this.packsProgressMarkup) {
        this.packsProgressStepsElement.innerHTML = markup;
        this.packsProgressMarkup = markup;
      }
    }

    render() {
      if (!this.itemsElement || !this.cart) return;
      const items = this.cart.items || [];
      const regularItems = this.cartItemsForDisplay(items.filter((item) => !this.isGiftItem(item))).sort((first, second) =>
        (this.addedItemOrder.get(this.cartItemDisplayIdentity(second)) || 0)
        - (this.addedItemOrder.get(this.cartItemDisplayIdentity(first)) || 0)
      );
      const giftOffer = this.giftOfferForQuantity(this.getSubscriptionPackQuantity(items));
      const subscriptionProductIds = items
        .filter((item) => !this.isGiftItem(item) && this.linePlanId(item) && Number(item.quantity) > 0)
        .map((item) => item.product_id);
      const giftItems = (giftOffer?.gifts || [])
        .filter((gift) => window.SaludSubscriptionConfig.isGiftEligible(gift, subscriptionProductIds));
      for (const item of items.filter(item => this.isGiftItem(item))) {
        if (item.properties?._subscription_gift === 'true' || item.properties?._bundle_builder_gift === 'true') continue;
        const variantId = item.variant_id || item.id;
        if (!giftItems.some(gift => String(gift.variantId) === String(variantId))) {
          giftItems.push({ variantId, title: item.product_title || item.title, image: item.image });
        }
      }
      const itemsMarkup = regularItems.map((item) => this.renderItem(item)).join('');
      if (itemsMarkup !== this.itemsMarkup || this.deferredFrequencyUpdate) {
        this.renderCartItems(regularItems, itemsMarkup);
        this.itemsMarkup = itemsMarkup;
      }
      const giftsMarkup = giftItems.map((gift, index) => this.renderGiftItem(gift, index)).join('');
      if (this.giftItemsElement && giftsMarkup !== this.giftsMarkup) {
        this.giftItemsElement.innerHTML = giftsMarkup;
        this.giftsMarkup = giftsMarkup;
      }
      if (this.giftsSectionElement) this.giftsSectionElement.hidden = giftItems.length === 0;
      const count = regularItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
      const checkoutTotal = this.getCheckoutTotal();
      if (this.itemCountElement) this.itemCountElement.textContent = count;
      if (this.checkoutTotalElement && !this.checkoutPending) this.checkoutTotalElement.textContent = this.formatMoney(checkoutTotal);
      if (this.checkoutOriginalTotalElement && !this.checkoutPending) {
        const originalTotal = this.getOriginalCartTotal();
        this.checkoutOriginalTotalElement.textContent = this.formatMoney(originalTotal);
        this.checkoutOriginalTotalElement.hidden = originalTotal <= checkoutTotal;
      }
      const hasRecommendations = this.renderRecommendations();
      if (this.footerElement) this.footerElement.hidden = regularItems.length === 0 && !hasRecommendations;
      if (this.checkoutBlockElement) this.checkoutBlockElement.hidden = regularItems.length === 0;
      if (this.emptyElement) this.emptyElement.hidden = regularItems.length > 0;
      this.renderProgress();
      this.updateCartBubble(count);
    }

    renderCartItems(items, markup) {
      const template = document.createElement('template');
      template.innerHTML = markup;
      const nextRows = [...template.content.children];
      const existingRows = [...this.itemsElement.children];
      const usedRows = new Set();
      const rows = [];
      const identity = (item) => this.cartItemDisplayIdentity(item);
      this.deferredFrequencyUpdate = false;

      items.forEach((item, index) => {
        const candidates = existingRows.filter((row) => !usedRows.has(row));
        let row = candidates.find((candidate) => candidate.dataset.key === String(item.key)
          || this.serverLineKeys.get(candidate.dataset.key) === item.key);
        if (!row) {
          const matchingRows = candidates.filter((candidate) => {
            const previousItem = this.renderedCartItems.get(candidate);
            return previousItem && identity(previousItem) === identity(item);
          });
          const matchingPlans = matchingRows.filter((candidate) => String(this.linePlanId(this.renderedCartItems.get(candidate)) || '') === String(this.linePlanId(item) || ''));
          if (matchingPlans.length === 1) row = matchingPlans[0];
          else if (matchingRows.length === 1) row = matchingRows[0];
        }
        if (row) this.updateCartNode(row, nextRows[index]);
        else row = nextRows[index];

        usedRows.add(row);
        this.renderedCartItems.set(row, structuredClone(item));
        rows.push(row);
      });

      for (const row of existingRows) {
        if (!usedRows.has(row)) row.remove();
      }
      const activeSelect = this.itemsElement.querySelector('select:focus');
      if (activeSelect && rows.some((row, index) => this.itemsElement.children[index] !== row)) {
        for (const row of rows) {
          if (row.parentElement !== this.itemsElement) this.itemsElement.append(row);
        }
        this.deferredFrequencyUpdate = true;
      } else {
        rows.forEach((row, index) => {
          const currentRow = this.itemsElement.children[index];
          if (currentRow !== row) this.itemsElement.insertBefore(row, currentRow || null);
        });
      }
    }

    updateCartNode(current, next) {
      if (current.nodeType !== next.nodeType || current.nodeName !== next.nodeName) {
        current.replaceWith(next);
        return;
      }
      if (current.nodeType !== Node.ELEMENT_NODE) {
        if (current.nodeValue !== next.nodeValue) current.nodeValue = next.nodeValue;
        return;
      }
      for (const attribute of [...current.attributes]) {
        if (!next.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
      }
      for (const attribute of [...next.attributes]) {
        if (current.getAttribute(attribute.name) !== attribute.value) current.setAttribute(attribute.name, attribute.value);
      }
      if (current instanceof HTMLSelectElement && current === document.activeElement && !next.disabled) {
        if (current.innerHTML !== next.innerHTML) this.deferredFrequencyUpdate = true;
        return;
      }
      const nextValue = next instanceof HTMLSelectElement ? next.value : null;
      const currentChildren = [...current.childNodes];
      const nextChildren = [...next.childNodes];
      nextChildren.forEach((child, index) => {
        if (currentChildren[index]) this.updateCartNode(currentChildren[index], child);
        else current.append(child);
      });
      for (const child of currentChildren.slice(nextChildren.length)) child.remove();
      if (current instanceof HTMLSelectElement && current.value !== nextValue) current.value = nextValue;
    }

    isGiftItem(item) {
      const properties = item?.properties || {};
      return properties._subscription_gift === 'true'
        || properties._gift === 'true'
        || properties._bundle_builder_gift === 'true';
    }

    renderGiftItem(gift, index) {
      const variantId = String(gift.variantId || gift.variant_id || gift.id || '');
      const title = gift.title || '';
      const description = gift.description || '';
      const originalPrice = String(gift.price || '');
      const label = this.translate('cart_drawer_v2.free_gift', 'FREE');
      const image = gift.image || '';

      return `<li class="cart-drawer-v2__gift-item" data-gift-item data-gift-variant-id="${this.escapeHtml(variantId)}" data-gift-index="${index}">
        ${image ? `<div class="cart-drawer-v2__gift-image-wrap"><img class="cart-drawer-v2__gift-image" src="${this.escapeHtml(image)}" alt="${this.escapeHtml(title)}" loading="lazy"></div>` : ''}
        <div class="cart-drawer-v2__gift-content"><div class="cart-drawer-v2__gift-info"><p class="cart-drawer-v2__gift-title">${this.escapeHtml(title)}</p>${description ? `<p class="cart-drawer-v2__gift-description">${this.escapeHtml(description)}</p>` : ''}</div><div class="cart-drawer-v2__gift-pricing">${originalPrice ? `<s class="cart-drawer-v2__gift-original-price">${this.escapeHtml(originalPrice)}</s>` : ''}<span class="cart-drawer-v2__gift-price">${this.escapeHtml(label)}</span></div></div>
      </li>`;
    }

    renderItem(item) {
      const key = String(item.key || '');
      const variantId = String(item.variant_id || item.id || '');
      const productTitle = this.productTitlesById.get(String(item.product_id || ''))
        || this.productTitlesById.get(String(item.variant_id || item.id || ''))
        || item.product_title
        || item.title
        || '';
      const itemUrl = item.url || (item.handle ? `/products/${item.handle}` : '#');
      const image = this.productImagesByVariant.get(variantId) || item.image || item.featured_image?.url || '';
      if (image) this.productImagesByVariant.set(variantId, image);
      const quantity = Number(item.quantity || 1);
      const unitPrice = Number(item.final_price ?? item.price ?? 0);
      const originalUnitPrice = this.getOriginalUnitPrice(item);
      const discountPercent = originalUnitPrice > unitPrice
        ? Math.round(((originalUnitPrice - unitPrice) / originalUnitPrice) * 100)
        : 0;
      const discountLabel = discountPercent > 0
        ? this.translate('cart_drawer_v2.discount_applied', '{{ percent }}% off applied!').replace('{{ percent }}', String(discountPercent))
        : '';
      const currentPlanId = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan_id || '';
      const planOptions = this.planOptionsByVariant.get(variantId) || [];
      const busy = this.isBlockedPendingItem(item) || this.checkoutPending === true;
      const productType = this.productTypesById.get(String(item.product_id || ''))
        || this.productTypesById.get(String(item.variant_id || item.id || ''))
        || item.product_type
        || '';
      const discountPrice = originalUnitPrice > unitPrice
        ? `<s data-item-original-price>${this.formatMoney(originalUnitPrice)}</s>`
        : '';
      const subscriptionButton = this.renderSubscriptionControl(item, planOptions, currentPlanId, busy);

      return `<li class="cart-drawer-v2__item" data-cart-item data-key="${this.escapeHtml(key)}" data-variant-id="${this.escapeHtml(variantId)}" data-quantity="${quantity}"${busy ? ' data-pending="true" aria-busy="true"' : ''}>
        <a class="cart-drawer-v2__image-link" href="${this.escapeHtml(itemUrl)}" tabindex="-1" aria-hidden="true">${image ? `<img class="cart-drawer-v2__image" src="${this.escapeHtml(image)}" alt="" loading="lazy">` : ''}</a>
        <div class="cart-drawer-v2__item-content">
          <div class="cart-drawer-v2__item-heading">
            <div><a class="cart-drawer-v2__product-title" href="${this.escapeHtml(itemUrl)}">${this.escapeHtml(productTitle)}</a></div>
            <div class="cart-drawer-v2__pricing">
              <div class="cart-drawer-v2__price">${discountPrice}<span data-item-price>${this.formatMoney(unitPrice)}</span></div>
              <span class="cart-drawer-v2__discount-applied" data-item-discount${discountPercent > 0 ? '' : ' hidden'}>${this.escapeHtml(discountLabel)}</span>
            </div>
          </div>
          ${productType ? `<p class="cart-drawer-v2__product-type">${this.escapeHtml(productType)}</p>` : ''}
          <div class="cart-drawer-v2__item-bottom"><div class="cart-drawer-v2__quantity" aria-label="${this.escapeHtml(this.translate('cart_drawer_v2.quantity', 'Quantity'))}"><button type="button" data-cart-action="decrease" aria-label="${this.escapeHtml(this.translate('cart_drawer_v2.decrease', 'Decrease quantity'))}"${busy || !this.canDecreaseItem(item) ? ' disabled' : ''}><svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M0.699219 4.7002H8.69922" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button><span data-item-quantity>${quantity}</span><button type="button" data-cart-action="increase" aria-label="${this.escapeHtml(this.translate('cart_drawer_v2.increase', 'Increase quantity'))}"${busy ? ' disabled' : ''}><svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M0.699219 4.7002H8.69922" stroke="white" stroke-width="1.4" stroke-linecap="round"/><path d="M4.69922 0.700195L4.69922 8.7002" stroke="white" stroke-width="1.4" stroke-linecap="round"/></svg></button></div>
            <button class="cart-drawer-v2__remove" type="button" data-cart-action="remove" aria-label="${this.escapeHtml(this.translate('cart_drawer_v2.remove_item', 'Remove {{ title }}').replace('{{ title }}', productTitle))}"${busy ? ' disabled' : ''}><svg xmlns="http://www.w3.org/2000/svg" width="13" height="15" viewBox="0 0 13 15" fill="none" aria-hidden="true" focusable="false"><path d="M3.85861 0.499229C3.93231 0.349122 4.04668 0.222775 4.18867 0.134535C4.33069 0.0462554 4.4946 -0.00035793 4.66179 2.06956e-06H8.04737C8.21457 -0.00035793 8.3785 0.0462554 8.5205 0.134535C8.6625 0.222769 8.77683 0.349122 8.85057 0.499229L9.05537 0.899229H11.7562C12.0652 0.917615 12.3433 1.09324 12.4927 1.36444C12.6422 1.63559 12.6422 1.96444 12.4927 2.23559C12.3433 2.50679 12.0652 2.68242 11.7562 2.7008H0.956147C0.70792 2.71565 0.464587 2.62737 0.2836 2.45684C0.102613 2.28632 0 2.04867 0 1.80002C0 1.55136 0.102607 1.31371 0.2836 1.14319C0.464587 0.972669 0.70792 0.884389 0.956147 0.899229H3.65375L3.85861 0.499229ZM0.956213 3.60003H11.7562V12.6016C11.7553 13.0789 11.5652 13.5363 11.2274 13.8734C10.8896 14.2106 10.4318 14.4 9.95463 14.4H2.7546C2.27763 14.4 1.82023 14.2106 1.48293 13.8733C1.14569 13.536 0.956213 13.0786 0.956213 12.6016V3.60003ZM3.65381 5.40163C3.53496 5.40163 3.42105 5.44882 3.33699 5.53283C3.25297 5.61684 3.20579 5.7308 3.20579 5.8496V12.1504C3.19517 12.2762 3.23777 12.4007 3.32319 12.4937C3.40865 12.5866 3.52912 12.6396 3.65537 12.6396C3.78167 12.6396 3.90215 12.5866 3.98761 12.4937C4.07303 12.4007 4.11563 12.2762 4.10501 12.1504V5.8496C4.10412 5.73049 4.05626 5.61658 3.97173 5.53263C3.88719 5.44872 3.77293 5.40163 3.65381 5.40163ZM6.35463 5.40163C6.2355 5.40163 6.12123 5.44872 6.0367 5.53262C5.95217 5.61658 5.90423 5.73049 5.90343 5.8496V12.1504C5.90343 12.3116 5.98943 12.4606 6.12897 12.5411C6.26863 12.6218 6.44057 12.6218 6.58023 12.5411C6.71983 12.4606 6.80583 12.3116 6.80583 12.1504V5.8496C6.80497 5.73048 6.75703 5.61657 6.6725 5.53262C6.58797 5.44872 6.4737 5.40163 6.35463 5.40163ZM9.05543 5.40163C8.9347 5.39903 8.8179 5.44502 8.7313 5.52918C8.6447 5.6134 8.59543 5.72882 8.59463 5.8496V12.1504C8.58403 12.2762 8.62663 12.4007 8.71203 12.4937C8.7975 12.5866 8.91797 12.6396 9.04423 12.6396C9.1705 12.6396 9.29097 12.5866 9.37643 12.4937C9.46183 12.4007 9.5045 12.2762 9.49383 12.1504V5.8496C9.49383 5.73246 9.44797 5.6199 9.36603 5.53616C9.28403 5.45241 9.17257 5.40414 9.05543 5.40163Z" fill="#442F2C"/></svg></button>
          </div>${subscriptionButton}<p class="cart-drawer-v2__item-error" role="alert" data-item-error hidden></p>
        </div>
      </li>`;
    }

    renderSubscriptionControl(item, plans, currentPlanId, busy) {
      if (!plans.length) return '';
      if (!currentPlanId) {
        const eligible = this.getSubscriptionPackQuantity() + Number(item.quantity || 0) >= this.minimumSubscriptionQuantity();
        if (!eligible) return '';
        return `<button class="cart-drawer-v2__subscribe" type="button" data-cart-action="subscribe"${busy ? ' disabled' : ''}>${this.escapeHtml(this.subscriptionButtonLabel(this.getSubscriptionPackQuantity() + Number(item.quantity || 0)))}</button>`;
      }

      const offer = this.offerForQuantity(this.getPackQuantity());
      const availablePlans = this.plansForOffer(plans, offer);
      const currentPlan = plans.find((plan) => String(plan.id) === String(currentPlanId));
      const allowedCurrentPlan = currentPlan && window.SaludSubscriptionConfig.isFrequencyAllowed(this.getPlanFrequency(currentPlan), offer);
      const uniquePlans = [...(allowedCurrentPlan ? [currentPlan] : []), ...availablePlans].filter((plan, index, all) => {
        const frequency = this.getPlanFrequency(plan);
        return all.findIndex((candidate) => this.getPlanFrequency(candidate) === frequency) === index;
      }).sort((first, second) =>
        Number(this.getPlanFrequency(first) || Infinity) - Number(this.getPlanFrequency(second) || Infinity)
      );
      const options = uniquePlans.map((plan) => `<option value="${this.escapeHtml(plan.id)}"${String(plan.id) === String(currentPlanId) ? ' selected' : ''}>${this.escapeHtml(this.getPlanLabel(plan))}</option>`).join('');
      return `<label class="cart-drawer-v2__frequency-control"><span class="visually-hidden">${this.escapeHtml(this.translate('cart_drawer_v2.delivery_frequency', 'Delivery frequency'))}</span><select class="cart-drawer-v2__frequency" data-cart-action="change-frequency" aria-label="${this.escapeHtml(this.translate('cart_drawer_v2.delivery_frequency', 'Delivery frequency'))}"${busy ? ' disabled' : ''}>${options}</select></label>`;
    }

    subscriptionButtonLabel(quantity) {
      const offer = this.offerForQuantity(quantity);
      return offer?.orderDiscount
        ? `${this.translate('cart_drawer_v2.subscribe_save', 'Subscribe & save')} ${Number(offer.orderDiscount)}%`
        : this.translate('cart_drawer_v2.subscribe_save', 'Subscribe & save');
    }

    updateCartBubble(count) {
      const bubble = document.querySelector('#cart-icon-bubble');
      if (!bubble) return;
      let countBubble = bubble.querySelector('.cart-count-bubble');
      if (count < 1) {
        countBubble?.remove();
        return;
      }
      if (!countBubble) {
        countBubble = document.createElement('div');
        countBubble.className = 'cart-count-bubble';
        countBubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
        bubble.append(countBubble);
      }
      const visible = countBubble.querySelector('[aria-hidden="true"]');
      const accessible = countBubble.querySelector('.visually-hidden');
      if (visible) visible.textContent = count < 100 ? count : '';
      if (accessible) {
        accessible.textContent = this.translate(count === 1 ? 'cart_drawer_v2.item_count_one' : 'cart_drawer_v2.item_count_other', '{{ count }} items').replace('{{ count }}', count);
      }
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.cart.currency || 'USD',
        }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    showItemError(key, message) {
      const row = [...this.querySelectorAll('[data-cart-item]')].find((item) => item.dataset.key === String(key));
      const error = row?.querySelector('[data-item-error]');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      } else {
        this.announce(message);
      }
    }

    announce(message) {
      if (this.statusElement) this.statusElement.textContent = message;
    }

    translate(key, fallback) {
      return this.strings?.[key] || fallback;
    }

    escapeHtml(value) {
      return String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
    }
  }

  customElements.define('salud-cart-drawer-v2', SaludCartDrawerV2);
}
